import {
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Palette,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { MoreVertical, Pencil, Search, Trash } from "lucide-react";

// Custom Components & Redux
import MenuPopover from "@components/MenuPopover";
import ConfirmDialog from "@components/ConfirmDialog";
import { getSectionLabelStyle, textStyle } from "@utils/cssStyles";
import {
  conversationActionMenuPopoverStyle,
  conversationRowTitleStyle,
  getActionRowIconButtonStyle,
  getConversationActionMenuItemStyle,
  getConversationRowStyle,
  historyEmptyStateStyle,
  historySearchFieldStyle,
} from "../../styles/askDostStyles";
import { useConversationHistory } from "../../hooks/useConversationHistory";
import { AskDostRenameModal } from "./AskDostRenameModal";
import { IConversation } from "../../types";
import { useChatActionMenu } from "@pages/askDost/hooks/useChatActionMenu";
import { useTheme } from "@mui/material";

interface IAskDostHistoryMenuProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  palette: Palette;
  translate: (text: string, options?: any) => string;
  onSelectConversation?: (conversationId: string, history?: any) => void;
}

export const AskDostHistoryMenu = ({
  anchorEl,
  onClose,
  palette,
  translate,
  onSelectConversation,
}: IAskDostHistoryMenuProps) => {
  const open = Boolean(anchorEl);
  const theme = useTheme();

  const {
    search,
    setSearch,
    isLoading,
    selectedConversationId,
    filteredConversations,
    resetSearch,
    handleSelect,
    pendingDeleteId,
    isDeleting,
    requestDelete,
    cancelDelete,
    confirmDelete,
    pendingRenameConv,
    isRenaming,
    requestRename,
    cancelRename,
    confirmRename,
  } = useConversationHistory({ translate, isOpen: open, onSelectConversation });

  const {
    actionMenuAnchor,
    openActionMenu,
    closeActionMenu,
    handleRenameClick,
    handleDeleteClick,
  } = useChatActionMenu(requestRename, requestDelete);

  const handleClose = () => {
    resetSearch();
    onClose();
  };

  const onSelect = (conv: any) => {
    handleSelect(conv);
    handleClose();
  };

  return (
    <>
      <MenuPopover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        disabledArrow
        sx={{ width: 310, p: 2, mt: 1 }}
      >
        <TextField
          autoFocus
          size="small"
          fullWidth
          hiddenLabel
          placeholder={translate("history_search_placeholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={14} color={palette.text.label} />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2, ...historySearchFieldStyle("8px") }}
        />

        <Typography
          variant="caption"
          sx={{
             ...textStyle("secondary", 400, 12)(theme),
            ...getSectionLabelStyle(palette.secondary.dark),
            pb: 1,
          }}
        >
          {translate("recent_chats")}
        </Typography>

        <Stack sx={{ maxHeight: 260, overflowY: "auto", mx: -1, px: 1 }}>
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
              <CircularProgress size={20} />
            </Box>
          ) : filteredConversations.length > 0 ? (
            filteredConversations.map((conv, index) => {
              const displayTitle =
                conv.title ||
                conv.name ||
                translate("chat_n", { id: conv.id || index });
              const isSelected = conv.id === selectedConversationId;
              const isMenuOpen = actionMenuAnchor?.conv.id === conv.id;

              return (
                <Box
                  key={conv.id || index}
                  onClick={() => onSelect(conv)}
                  sx={getConversationRowStyle(palette, isSelected)}
                >
                  <Typography
                    variant="body2"
                    noWrap
                    sx={conversationRowTitleStyle(palette, isSelected)}
                  >
                    {displayTitle}
                  </Typography>

                  <IconButton
                    edge="end"
                    size="small"
                    className="askdost-history-action-btn"
                    onClick={(e) => openActionMenu(e, conv)}
                    sx={getActionRowIconButtonStyle(palette, isMenuOpen)}
                  >
                    <MoreVertical size={18} />
                  </IconButton>
                </Box>
              );
            })
          ) : (
            <Typography
              variant="caption"
              sx={{ px: 1, py: 1.5, ...historyEmptyStateStyle(palette) }}
            >
              {search
                ? translate("history_no_match", { query: search })
                : translate("no_conversations_found")}
            </Typography>
          )}
        </Stack>
      </MenuPopover>

      <MenuPopover
        open={!!actionMenuAnchor}
        anchorEl={actionMenuAnchor?.el || null}
        onClose={closeActionMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        disabledArrow
        sx={conversationActionMenuPopoverStyle}
      >
        <MenuItem
          onClick={handleRenameClick}
          sx={getConversationActionMenuItemStyle(palette)}
        >
          <Pencil size={16} />
          {translate("rename")}
        </MenuItem>
        <MenuItem
          onClick={handleDeleteClick}
          sx={getConversationActionMenuItemStyle(palette)}
        >
          <Trash size={16} />
          {translate("delete")}
        </MenuItem>
      </MenuPopover>

      <ConfirmDialog
        open={!!pendingDeleteId}
        title={translate("delete_conversation_title")}
        content={translate("delete_conversation_message")}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        confirmLoading={isDeleting}
      />

      <AskDostRenameModal
        open={!!pendingRenameConv}
        initialTitle={pendingRenameConv?.title || ""}
        isRenaming={isRenaming}
        translate={translate}
        onCancel={cancelRename}
        onSave={confirmRename}
      />
    </>
  );
};